import GLAccountDelegate from '../delegates/GLAccount.delegate';
import { CASHFLOW_GROUPING, GL_TYPE } from '../treasury.constants';
import { calculateDateRange } from '../../../utils/dates';

/**
 * Owner of every GL Account section in the Cashflow Management screen
 * (account balance, funding recommendation, KPI row, cashflow chart,
 * aging receivables).
 *
 * Plain JS class — NOT a React component. Mirrors the pattern in
 * `configurations/controllers/Segmentation.controller.js`: the constructor
 * receives the host `page` (the React orchestrator) and every mutation
 * routes through `setState(partial)` which calls `page.forceUpdate()`.
 *
 * The orchestrator owns user-controlled context (`glType`, `timeFrame`,
 * `dateRange`); this controller is a pure side-effect bag that fetches
 * the right slice of mock/API data and exposes view-ready props.
 */
class GLAccountController extends GLAccountDelegate {
    constructor(page) {
        super();
        this.page = page;
        this.state = {
            accountData: null,
            accountLoaded: false,
            fundingData: null,
            fundingLoaded: false,
            kpis: null,
            kpisLoaded: false,
            cashflowData: null,
            cashflowLoaded: false,
            cashflowGrouping: CASHFLOW_GROUPING.MONTHLY,
            aging: null,
            agingLoaded: false,
        };
    }

    setState(partial) {
        const next = typeof partial === 'function' ? partial(this.state) : partial;
        if (!next) return;
        this.state = { ...this.state, ...next };
        if (this.page && typeof this.page.forceUpdate === 'function') {
            this.page.forceUpdate();
        }
    }

    /**
     * Resolves the right `data.fetchX` for the active GL tab. Both
     * fetchers expose the same `{ section, start, end, grouping }`
     * contract.
     */
    getFetcher(glType) {
        return glType === GL_TYPE.FEE
            ? this.page.data.fetchFeeGL.bind(this.page.data)
            : this.page.data.fetchAdvanceGL.bind(this.page.data);
    }

    /**
     * Loads every GL section in parallel. Called by the orchestrator on
     * mount, on tab change, and on period change.
     */
    async refresh({ glType, timeFrame, dateRange }) {
        const fetcher = this.getFetcher(glType);
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        const grouping = this.state.cashflowGrouping;

        this.setAllLoading();

        await Promise.all([
            fetcher({ section: 'account' })
                .then((d) => this.setAccount(d))
                .catch((error) => this.handleSectionError('account', error)),
            fetcher({ section: 'funding' })
                .then((d) => this.setFunding(d))
                .catch((error) => this.handleSectionError('funding', error)),
            fetcher({ section: 'kpis', start, end })
                .then((d) => this.setKpis(d))
                .catch((error) => this.handleSectionError('kpis', error)),
            fetcher({ section: 'cashflow', start, end, grouping })
                .then((d) => this.setCashflow(d))
                .catch((error) => this.handleSectionError('cashflow', error)),
            fetcher({ section: 'aging', start, end })
                .then((d) => this.setAging(d))
                .catch((error) => this.handleSectionError('aging', error)),
        ]);
    }

    /**
     * Re-fetches only the cashflow slice when the user toggles
     * Quarterly / Monthly. The other sections do not depend on the
     * grouping, so they stay as-is.
     */
    async handleCashflowGroupingChange(grouping) {
        if (grouping === this.state.cashflowGrouping) return;
        this.setCashflowGrouping(grouping);
        const { glType, timeFrame, dateRange } = this.page.getGLContext();
        const fetcher = this.getFetcher(glType);
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        this.setState({ cashflowLoaded: false });
        try {
            const cashflowData = await fetcher({
                section: 'cashflow', start, end, grouping,
            });
            this.setCashflow(cashflowData);
        } catch (error) {
            this.handleSectionError('cashflow', error);
        }
    }

    /**
     * Logs API failures and marks the section loaded with no data so the
     * skeleton clears instead of showing mock figures or spinning forever.
     */
    handleSectionError(section, error) {
        console.error(`[GLAccount] ${section} fetch failed`, error);
        switch (section) {
            case 'account':
                this.setState({ accountData: null, accountLoaded: true });
                break;
            case 'funding':
                this.setState({ fundingData: null, fundingLoaded: true });
                break;
            case 'kpis':
                this.setState({ kpis: null, kpisLoaded: true });
                break;
            case 'cashflow':
                this.setState({ cashflowData: null, cashflowLoaded: true });
                break;
            case 'aging':
                this.setState({ aging: null, agingLoaded: true });
                break;
            default:
                break;
        }
    }

    /**
     * Returns props ready to drop into the dumb `GLAccountSection`
     * view. Each card receives just what it needs plus a `loaded`
     * flag so the view can show a skeleton without owning fetch state.
     */
    getViewProps() {
        const {
            accountData, accountLoaded,
            fundingData, fundingLoaded,
            kpis, kpisLoaded,
            cashflowData, cashflowLoaded, cashflowGrouping,
            aging, agingLoaded,
        } = this.state;

        return {
            account: { data: accountData, loaded: accountLoaded },
            funding: { data: fundingData, loaded: fundingLoaded },
            kpis: { data: kpis, loaded: kpisLoaded },
            cashflow: {
                data: cashflowData,
                loaded: cashflowLoaded,
                grouping: cashflowGrouping,
                onGroupingChange: (g) => this.handleCashflowGroupingChange(g),
            },
            aging: { data: aging, loaded: agingLoaded },
        };
    }
}

export default GLAccountController;
